/**
 * Point 29 — the three pipeline counts, visible from the inbox.
 *
 * A purchaser working the mailbox is one screen away from the board and still has to go and look
 * to answer "what is out at RFQ, what is waiting to be compared, what has been quoted". These are
 * those three numbers, derived from the enquiry list rather than fetched: the mail screen already
 * has a `useEnquiries` call available to it, so the strip costs nothing.
 *
 * ---- What is deliberately NOT counted ----
 *
 * Won, Rejected and Archived. Each of these numbers exists to answer "how much is in flight", and
 * a bucket that only ever grows stops answering it — a year in, "quotations: 812" tells nobody
 * anything. Decided work leaves the count.
 *
 * Status is read through `effectiveStatus`, the same fold the board applies, so a quotation past
 * thirty days drops out of the count exactly as it drops off the board. The strip and the board it
 * links to always agree.
 */

import { effectiveStatus } from './format'
import type { EnquiryStatus, EnquirySummary } from './types'

export interface InboxCounts {
  rfq: number
  comparison: number
  quotation: number
}

/**
 * The statuses behind each bucket, exported so the strip's LINK can filter the board to exactly
 * what its number counted. Listing them again at the call site is how a count and the screen it
 * opens come to disagree.
 */
export const INBOX_COUNT_STATUSES: Record<keyof InboxCounts, EnquiryStatus[]> = {
  rfq: ['RFQ Sent'],
  // Quotes are in and a comparison is the next act — whether or not one has been saved yet.
  comparison: ['Quotes Received', 'Comparison Done'],
  quotation: ['Quotation Sent'],
}

export function inboxCounts(rows: readonly EnquirySummary[]): InboxCounts {
  const counts: InboxCounts = { rfq: 0, comparison: 0, quotation: 0 }

  for (const row of rows) {
    const status = effectiveStatus(row.status, row.age_days)
    for (const bucket of Object.keys(counts) as (keyof InboxCounts)[]) {
      if (INBOX_COUNT_STATUSES[bucket].includes(status)) counts[bucket] += 1
    }
  }

  return counts
}
